import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, Shield, ShieldCheck, Radar, Activity,
  HardDrive, Mail, Server, Clock, MapPin, Zap, Lock, Users,
  Gauge, Layers, ScanSearch,
} from "lucide-react";
import { Eyebrow, SectionHeading } from "@/components/Section";
import { SEO } from "@/components/SEO";
import { MsExplainers } from "@/components/explainers/MsExplainers";

const SITE_URL = "https://it-servicepoint.eu";

const SERVICES = [
  {
    slug: "managed-firewall",
    number: "01",
    icon: Shield,
    title: "Managed Firewall",
    subtitle: "Securepoint UTM – 24/7 gemanagt aus Deutschland",
    image: "/wp-images/managed-security.jpg",
    intro:
      "Ihre Firewall ist nur so gut wie ihre Konfiguration. Deshalb übernehmen wir für Sie den kompletten Betrieb der Securepoint UTM: Regelwerk, Patches, Updates, Monitoring und Incident Response – rund um die Uhr, aus einem Rechenzentrum in Deutschland.",
    highlights: [
      "Konfigurations-Pflege, Patching und Changes",
      "24/7 Monitoring durch zertifizierte Analysten",
      "Portfilter, Regelwerk, Routing und Web-Proxy",
      "VPN Site-to-Site (IPsec, OpenVPN, WireGuard)",
      "VPN Site-to-End über Securepoint ASC",
      "Vor-Ort-Service bei Hardware-Defekt",
    ],
    audience: "Für kleine und mittelständische Unternehmen ab 5 Arbeitsplätzen",
    keywords: "Managed Firewall Fulda, UTM-Firewall Hessen, Securepoint Partner",
  },
  {
    slug: "managed-antivirus-pro",
    number: "02",
    icon: ShieldCheck,
    title: "Managed Antivirus Pro",
    subtitle: "Nächste Generation Endpoint-Schutz mit Application Control",
    image: "/wp-images/it-sicherheit.jpg",
    intro:
      'Klassisches Antivirus stoppt das Bekannte. Doch 2026 ist Schadcode oft unbekannt: polymorph, gezielt oder lieferketten-getarnt. Managed Antivirus Pro kombiniert eine leistungsstarke AV-Engine (G DATA & IKARUS) mit Application Control – dem Whitelisting-Verfahren, das die Frage umkehrt: nicht „was ist bekannt?" – sondern „was darf laufen?".',
    highlights: [
      "AV-Engine Made in Germany (G DATA + IKARUS)",
      "Application Control mit Trust-Level-Datenbank",
      "Hash-basierte Datei-Identität + Signatur-Prüfung",
      "Default-Deny: unbekannte Software wird blockiert",
      "USB-Device-Control inklusive",
      "Cloud-Vertrauensdatenbank für bekannte Applikationen",
      "Zentrale Verwaltung im Admin Wizard",
    ],
    audience: "Für Unternehmen mit strengen Compliance-Anforderungen (NIS-2, DSGVO, KRITIS)",
    keywords: "Managed Antivirus, Application Control, Endpoint Security, EDR Hessen",
  },
  {
    slug: "managed-xdr",
    number: "03",
    icon: Radar,
    title: "Managed XDR",
    subtitle: "24/7 Extended Detection & Response – Analysten in Deutschland",
    image: "/wp-images/schwachstellen-banner.jpg",
    intro:
      "Cyber-Angriffe finden nachts, am Wochenende und im Urlaub statt. Managed XDR ist Ihr 24/7-Sicherheitszentrum: Analysten in Bochum und Wien überwachen kontinuierlich Ihre gesamte IT – Netzwerk, Endpoints, Cloud und E-Mails – erkennen Angriffe in Echtzeit und reagieren, bevor Schaden entsteht. Made in Europe. Kein Niedriglohnland.",
    highlights: [
      "24/7 Detection und Response ohne Ausnahmen",
      "Analysten in Deutschland (Bochum) & Österreich (Wien)",
      "Web-Console zur Live-Einsicht in Vorfälle",
      "Vollständige Abdeckung: Netzwerk, Endpoint, Cloud, Mail",
      "PRO: Jeder Vorfall wird persönlich überprüft",
      "PRO: Aktiver Anruf bei kritischem Angriff",
      "PRO: Dedizierter Ansprechpartner + regelmäßige Config-Checks",
    ],
    audience: "Für alle Unternehmen ab 15 Mitarbeitern, insbesondere KRITIS & NIS-2-pflichtige",
    keywords: "Managed XDR, SOC as a Service, 24/7 Cyber Defense, MDR Deutschland",
  },
  {
    slug: "managed-backup",
    number: "04",
    icon: HardDrive,
    title: "Managed Backup",
    subtitle: "Unified Backup mit Air-Gap – Restore in unter 15 Minuten",
    image: "/wp-images/managed-backup.jpg",
    intro:
      "Backup ist die letzte Verteidigungslinie gegen Ransomware. Wir sichern Ihre Daten nach dem 3-2-1-Prinzip mit Air-Gap-Isolation: 3 Kopien, auf 2 unterschiedlichen Medien, 1 davon offline. Alles in deutschen Rechenzentren, alles automatisiert, alles getestet – damit im Ernstfall der Restore in unter 15 Minuten steht.",
    highlights: [
      "3-2-1 Backup-Strategie mit Air-Gap",
      "Notfall-Restore-Plan mit RTO < 15 Minuten",
      "Active Protection gegen Ransomware",
      "Server, Clients, Microsoft 365 & Datenbanken",
      "Regelmäßige Restore-Tests inklusive",
      "Hosting in TÜV-zertifizierten deutschen Rechenzentren",
      "Compliance mit DSGVO, GoBD & NIS-2",
    ],
    audience: "Pflichtprogramm für alle Unternehmen – von 5 bis 500 Mitarbeitern",
    keywords: "Managed Backup Hessen, Ransomware Schutz, Backup as a Service, Restore Fulda",
  },
  {
    slug: "managed-email-exchange",
    number: "05",
    icon: Mail,
    title: "Managed Email Exchange & Mailarchivierung",
    subtitle: "Rechtssichere E-Mail-Kommunikation aus deutschen Rechenzentren",
    image: "/wp-images/backup-cube.jpg",
    intro:
      "E-Mail ist geschäftskritisch – und rechtlich sensibel. Wir betreiben Ihren Exchange-Server (Microsoft 365 oder eigenständig) inklusive revisionssicherer Mailarchivierung über MailStore. Damit erfüllen Sie automatisch GoBD-Anforderungen, halten steuerrelevante E-Mails 10 Jahre revisionssicher vor und schützen sich gleichzeitig vor Phishing, Spam und Ransomware-Anhängen.",
    highlights: [
      "Managed Exchange (M365 oder On-Premise)",
      "Revisionssichere Mailarchivierung via MailStore",
      "Automatische Aufbewahrung nach GoBD (10 Jahre)",
      "Anti-Phishing, Anti-Spam, Anti-Malware",
      "Content Filter & Cloud Shield",
      "Verschlüsselte E-Mail-Kommunikation (S/MIME, PGP)",
      "Hosting ausschließlich in deutschen Rechenzentren",
    ],
    audience: "Verpflichtend für alle Unternehmen mit steuerrelevanter E-Mail-Kommunikation",
    keywords: "Managed Exchange, Mailarchivierung GoBD, MailStore Partner, E-Mail Security Fulda",
  },
];

const BENEFITS = [
  { icon: Clock, title: "Planbare Monatsraten", body: "Statt unkalkulierbarer Notfall-Rechnungen: ein fester Monatsbetrag pro Service." },
  { icon: Users, title: "Sie behalten Ihren Ansprechpartner", body: "Ein Team, eine Rufnummer, eine Rechnung – über alle Services hinweg." },
  { icon: MapPin, title: "Made in Germany & Europa", body: "Analysten, Rechenzentren und Support ausschließlich in D/A/EU." },
  { icon: Gauge, title: "SLA mit Reaktionszeit unter 40 Min", body: "Vertraglich zugesichert, 7 Tage die Woche, im Notfall 24/7." },
  { icon: Layers, title: "Alles aus einer Hand", body: "UTM, AV, XDR, Backup und Mail – ein Hersteller, alles integriert." },
  { icon: Lock, title: "Compliance ab Werk", body: "NIS-2, DSGVO, GoBD, ISO 27001-Methodik – bereits in unseren Prozessen verankert." },
];

const FAQ = [
  {
    q: 'Was bedeutet „Managed Service" bei IT-Servicepoint?',
    a: '„Managed" heißt: Sie bekommen den Service als monatlich abgerechnete Rundum-Leistung. Wir übernehmen Konfiguration, Betrieb, Monitoring, Updates, Patches, Incident Response und Reporting. Sie haben einen Ansprechpartner, eine Rechnung und einen SLA – ohne interne IT-Ressourcen aufzubauen.',
  },
  {
    q: "Kann ich einzelne Services buchen oder muss ich alle nehmen?",
    a: "Sie können jeden Service einzeln buchen. Empfehlung: Die Kombination aus Managed Firewall + Managed Antivirus Pro + Managed XDR bildet den goldenen Standard für den Endpoint- und Perimeter-Schutz. Managed Backup und Managed Exchange kommen typischerweise auf Wunsch dazu.",
  },
  {
    q: "In welchen Regionen sind Sie aktiv?",
    a: "Unser Hauptstandort ist in Schenklengsfeld bei Bad Hersfeld. Vor-Ort-Service bieten wir in Osthessen (Fulda, Bad Hersfeld, Hünfeld), Nordhessen (Kassel), Thüringen (Erfurt) und Unterfranken (Würzburg). Alle Managed Services sind bundesweit verfügbar, da Monitoring und Response remote erfolgen.",
  },
  {
    q: "Welche Zertifizierungen habt Ihr?",
    a: "Wir sind Securepoint Partner, Greenbone Certified und Acronis Backup Partner. Unsere Prozesse orientieren sich an der ISO 27001-Methodik. Bei Bedarf liefern wir NIS-2- und DSGVO-Nachweise für Ihre Compliance-Dokumentation.",
  },
  {
    q: "Wie schnell reagiert Ihr im Ernstfall?",
    a: "Vertraglich zugesichert unter 40 Minuten Reaktionszeit. Managed XDR reagiert 24/7 automatisch auf detektierte Angriffe. Bei Managed XDR Pro rufen wir Sie bei kritischen Vorfällen aktiv an.",
  },
  {
    q: "Sind eure Daten wirklich in Deutschland?",
    a: "Ja. Wir hosten ausschließlich in TÜV-zertifizierten Rechenzentren in Deutschland. Managed XDR-Analysten sitzen in Bochum (Securepoint × G DATA) und Wien (IKARUS Threat-Intel). Kein Niedriglohnland, kein Cloud-Outsourcing außerhalb der EU.",
  },
];

export const ManagedServices = () => {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "@id": `${SITE_URL}/managed-services#services`,
      name: "Managed Services",
      itemListElement: SERVICES.map((s, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "Service",
          name: s.title,
          description: s.intro,
          provider: { "@id": `${SITE_URL}/#organization` },
          serviceType: s.title,
          areaServed: { "@type": "Country", name: "DE" },
        },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Start", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Managed Services", item: `${SITE_URL}/managed-services` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <div data-testid="managed-services-page" className="relative">
      <SEO
        title="Managed Services · Managed Firewall, XDR, Antivirus Pro, Backup, Exchange"
        description="Alle Managed Security & IT Services aus einer Hand: Managed Firewall, Antivirus Pro, XDR (24/7), Backup und Exchange mit Mailarchivierung. Made in Germany – für den Mittelstand in Hessen und ganz Deutschland."
        keywords="Managed Services, Managed Firewall, Managed Antivirus, Managed XDR, Managed Backup, Managed Exchange, Mailarchivierung, IT-Servicepoint, Fulda, Bad Hersfeld, Kassel, Erfurt, Würzburg, Hessen, Securepoint Partner, MSP Deutschland"
        jsonLd={jsonLd}
        type="service"
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-ink text-white border-b border-line">
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "radial-gradient(ellipse at 15% 20%, rgba(29,169,218,0.35), transparent 55%), radial-gradient(ellipse at 85% 80%, rgba(164,197,57,0.25), transparent 55%)",
        }} />
        <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.14) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }} />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 py-20 sm:py-32">
          <Eyebrow className="text-accent">Managed Services · Made in Germany</Eyebrow>
          <h1 className="mt-6 font-display font-black text-5xl sm:text-7xl lg:text-8xl tracking-tightest leading-[0.92] max-w-5xl">
            Ihre IT-Sicherheit.<br />
            <span className="text-accent">Unser 24/7-Job.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg sm:text-xl text-white/80 leading-relaxed">
            Firewall, Antivirus, Angriffserkennung, Backup und E-Mail – als monatlich abgerechneter Rundum-Service. Ohne interne IT-Abteilung. Ohne versteckte Kosten. Mit Analysten und Rechenzentren <strong className="text-white">ausschließlich in Deutschland und Europa</strong>.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link
              to="/kontakt"
              data-testid="ms-hero-cta-primary"
              className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-accent text-ink font-medium hover:bg-accent/90 transition-colors"
            >
              Kostenfreies Beratungsgespräch
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href="#services"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/40 text-white hover:bg-white/10 transition-colors font-medium"
            >
              Zu den Services
            </a>
          </div>

          {/* Quick service pills */}
          <div className="mt-16 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {SERVICES.map((s) => (
              <a
                key={s.slug}
                href={`#${s.slug}`}
                data-testid={`ms-pill-${s.slug}`}
                className="group border border-white/15 bg-white/5 p-4 hover:bg-white/10 hover:border-accent/40 transition-colors"
              >
                <s.icon size={18} className="text-accent" />
                <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">{s.number}</div>
                <div className="mt-1 font-display font-bold text-sm text-white group-hover:text-accent transition-colors leading-tight">{s.title}</div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="bg-white border-b border-line">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-28">
          <SectionHeading
            eyebrow="Warum Managed Services?"
            title="Sicherheit, ohne selbst zum Sicherheitsspezialisten zu werden."
            sub="Sie konzentrieren sich auf Ihr Kerngeschäft. Wir kümmern uns darum, dass Ihre IT läuft – und sicher bleibt."
          />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFITS.map((b, i) => (
              <div key={b.title} className="border border-line bg-white p-8 lift" data-testid={`ms-benefit-${i}`}>
                <div className="w-11 h-11 bg-soft border border-line flex items-center justify-center text-primary mb-5">
                  <b.icon size={20} />
                </div>
                <h3 className="font-display font-bold text-lg text-ink">{b.title}</h3>
                <p className="mt-3 text-muted text-sm leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="bg-soft border-b border-line">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-32">
          <SectionHeading
            eyebrow="Unser Portfolio"
            title="5 Services · 1 Partner · 0 Kompromisse."
            sub="Alle Bausteine, die ein moderner Mittelständler für eine widerstandsfähige IT braucht – kombinierbar, planbar, DSGVO-konform."
          />

          <div className="mt-20 space-y-24">
            {SERVICES.map((s, idx) => (
              <article
                key={s.slug}
                id={s.slug}
                data-testid={`ms-service-${s.slug}`}
                className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start scroll-mt-24"
              >
                {/* Image side */}
                <div className={`lg:col-span-5 ${idx % 2 === 1 ? "lg:order-2" : ""}`}>
                  <div className="relative overflow-hidden border border-line bg-white">
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={s.image}
                        alt={`${s.title} – IT-Servicepoint`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div className="absolute top-4 left-4 bg-primary text-white px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em]">
                      {s.number} · Managed Service
                    </div>
                  </div>

                  {/* Audience card */}
                  <div className="mt-4 border border-line bg-white p-5">
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-primary">Zielgruppe</div>
                    <div className="mt-2 text-ink text-sm">{s.audience}</div>
                  </div>
                </div>

                {/* Content side */}
                <div className={`lg:col-span-7 ${idx % 2 === 1 ? "lg:order-1" : ""}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white border border-line flex items-center justify-center text-primary">
                      <s.icon size={22} />
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted">Service {s.number}</div>
                  </div>
                  <h2 className="mt-5 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tightest text-ink leading-[1.05]">
                    {s.title}
                  </h2>
                  <p className="mt-3 font-display text-lg text-primary">{s.subtitle}</p>
                  <p className="mt-6 text-muted text-lg leading-relaxed">{s.intro}</p>

                  <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {s.highlights.map((h) => (
                      <li key={h} className="flex items-start gap-2 text-ink text-sm">
                        <CheckCircle2 size={18} className="text-primary shrink-0 mt-0.5" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-10 flex flex-col sm:flex-row gap-3">
                    <Link
                      to="/kontakt"
                      data-testid={`ms-service-cta-${s.slug}`}
                      className="group inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white font-medium hover:bg-primary-hover transition-colors"
                    >
                      Beratung zu {s.title.split(" & ")[0]}
                      <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                    </Link>
                    <a
                      href="tel:+4966299153890"
                      className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-line bg-white text-ink hover:border-primary hover:text-primary transition-colors"
                    >
                      +49 (0) 6629 9153890
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <MsExplainers />

      {/* PROCESS */}
      <section className="bg-white border-b border-line">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-28">
          <SectionHeading
            eyebrow="So starten wir"
            title="In 4 Schritten zum sorglosen Betrieb."
          />
          <ol className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: "01", title: "Erstgespräch", body: "30 Minuten unverbindlich – wir hören zu, verstehen Ihre Umgebung und Ihre Ziele." },
              { step: "02", title: "IT-Bestandsaufnahme", body: "Wir prüfen bestehende Systeme, Verträge und Schwachstellen (kostenfrei bei Beauftragung)." },
              { step: "03", title: "Angebot & Onboarding", body: "Festpreis-Angebot in 3 Werktagen. Onboarding in 2–4 Wochen inkl. Übernahme laufender Systeme." },
              { step: "04", title: "Betrieb & Reporting", body: "Monitoring läuft. Sie bekommen monatliche Reports und Zugriff auf unser Kundenportal." },
            ].map((s, i) => (
              <li key={s.step} className="border border-line bg-white p-7" data-testid={`ms-process-${i}`}>
                <span className="font-display font-black text-4xl text-accent">{s.step}</span>
                <h4 className="mt-4 font-display font-bold text-lg text-ink">{s.title}</h4>
                <p className="mt-3 text-muted text-sm leading-relaxed">{s.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-soft border-b border-line">
        <div className="max-w-4xl mx-auto px-6 sm:px-12 py-24 sm:py-28">
          <Eyebrow>Häufige Fragen</Eyebrow>
          <h2 className="mt-4 font-display font-black text-3xl sm:text-5xl tracking-tightest text-ink">FAQ.</h2>
          <div className="mt-12 space-y-4">
            {FAQ.map((f, i) => (
              <details key={i} className="border border-line bg-white p-6 group" data-testid={`ms-faq-${i}`}>
                <summary className="font-display font-bold text-lg text-ink cursor-pointer flex items-center justify-between gap-4">
                  {f.q}
                  <span className="text-primary group-open:rotate-45 transition-transform text-xl">+</span>
                </summary>
                <p className="mt-4 text-muted leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.15) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }} />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 py-20 sm:py-28 text-center">
          <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tightest leading-[1.05] max-w-3xl mx-auto">
            Sprechen wir über Ihre IT.
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-white/80 text-lg">
            30 Minuten Erstgespräch · kostenfrei · unverbindlich. Wir hören zu und liefern Ihnen einen ehrlichen Vorschlag, welche Managed Services in Ihrem Setup wirklich Sinn ergeben.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/kontakt"
              data-testid="ms-cta-contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-accent text-ink font-medium hover:bg-accent/90 transition-colors"
            >
              Termin vereinbaren <ArrowRight size={18} />
            </Link>
            <a
              href="tel:+4966299153890"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/40 text-white hover:bg-white/10 transition-colors"
            >
              Jetzt anrufen · +49 (0) 6629 9153890
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
